import { FileText, Download, File, Image, FileSpreadsheet, FileCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';

interface FileAttachmentProps {
  fileUrl: string;
  fileName: string;
  fileType: string;
  caption?: string | null;
  isOwn?: boolean;
}

export function FileAttachment({
  fileUrl,
  fileName,
  fileType,
  caption,
  isOwn = false,
}: FileAttachmentProps) {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const isImage = fileType.startsWith('image/');
  const isPDF = fileType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
  const isExcel = fileType.includes('spreadsheet') || 
    fileType.includes('excel') || 
    fileName.toLowerCase().match(/\.(xls|xlsx)$/);
  const isWord = fileType.includes('word') || 
    fileType.includes('document') || 
    fileName.toLowerCase().match(/\.(doc|docx)$/);
  const isText = fileType.includes('text') || 
    fileName.toLowerCase().match(/\.(txt|csv)$/);

  const getFileIcon = () => {
    if (isImage) return Image;
    if (isPDF) return FileText;
    if (isExcel) return FileSpreadsheet;
    if (isWord || isText) return FileCode;
    return File;
  };

  const getFileTypeLabel = () => {
    if (isPDF) return 'PDF';
    if (isExcel) return 'Excel';
    if (isWord) return 'Word';
    if (isText) return 'Text';
    if (isImage) return 'Image';
    return fileType.split('/')[1]?.toUpperCase() || 'File';
  };

  const handleDownload = () => {
    window.open(fileUrl, '_blank');
  };

  const handleImageClick = () => {
    if (isImage) {
      window.open(fileUrl, '_blank');
    }
  };

  const FileIcon = getFileIcon();

  if (isImage) {
    return (
      <div className="mt-2">
        <div className="relative">
          {imageLoading && (
            <div className="absolute inset-0 bg-slate-200 animate-pulse rounded-lg flex items-center justify-center">
              <Image className="w-8 h-8 text-slate-400" />
            </div>
          )}
          {!imageError ? (
            <img
              src={fileUrl}
              alt={caption || fileName}
              className={`max-w-xs rounded-lg cursor-pointer hover:opacity-90 transition ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
              onClick={handleImageClick}
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageLoading(false);
                setImageError(true);
              }}
            />
          ) : (
            <div className="max-w-xs rounded-lg bg-slate-200 p-4 flex flex-col items-center justify-center gap-2">
              <Image className="w-8 h-8 text-slate-400" />
              <p className={`text-xs ${isOwn ? 'text-white' : 'text-slate-600'}`}>
                Failed to load image
              </p>
            </div>
          )}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <p className={`text-xs font-medium ${isOwn ? 'text-white' : 'text-slate-700'}`}>
            {fileName}
          </p>
          <Badge variant="outline" className={`text-xs ${isOwn ? 'border-white/30 text-white bg-white/10' : ''}`}>
            {getFileTypeLabel()}
          </Badge>
        </div>
        {caption && (
          <p className={`text-xs mt-1 ${isOwn ? 'text-white/90' : 'text-slate-600'}`}>
            {caption}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={`mt-2 p-3 rounded-lg border flex items-center gap-3 ${isOwn ? 'bg-white/10 border-white/30' : 'bg-slate-50 border-slate-200'}`}>
      <div className="flex-shrink-0">
        <FileIcon className={`w-5 h-5 ${isOwn ? 'text-white' : 'text-slate-500'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className={`text-sm font-medium truncate ${isOwn ? 'text-white' : 'text-slate-900'}`}>
            {fileName}
          </p>
          <Badge variant="outline" className={`text-xs ${isOwn ? 'border-white/30 text-white bg-white/10' : ''}`}>
            {getFileTypeLabel()}
          </Badge>
        </div>
        {caption && (
          <p className={`text-xs mb-1 ${isOwn ? 'text-white/90' : 'text-slate-600'}`}>
            {caption}
          </p>
        )}
        <p className={`text-xs ${isOwn ? 'text-white/80' : 'text-slate-500'}`}>
          {fileType}
        </p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleDownload}
        className={`flex-shrink-0 ${isOwn ? 'text-white hover:bg-white/20' : ''}`}
      >
        <Download className={`w-4 h-4 ${isOwn ? 'text-white' : ''}`} />
      </Button>
    </div>
  );
}
